import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { getCheckoutStatus } from "@/lib/api";
import { CheckCircle, CircleNotch, XCircle, Lightning, Package } from "@phosphor-icons/react";

const MAX_POLLS = 8;
const POLL_INTERVAL = 2500;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "polling", data: null, error: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus({ state: "error", error: "No session ID found in URL." });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= MAX_POLLS) {
        setStatus({ state: "timeout", data: null });
        return;
      }
      attemptsRef.current += 1;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus({ state: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setStatus({ state: "expired", data });
          return;
        }
        // still pending
        setStatus({ state: "polling", data });
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (cancelled) return;
        setStatus({ state: "error", error: e.response?.data?.detail || "Status check failed" });
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-arena-bg text-arena-text font-mono flex items-center justify-center p-6" data-testid="checkout-success">
      <div className="w-full max-w-lg border border-arena-border bg-arena-surface p-8">
        <div className="flex items-center gap-2 mb-6">
          <Lightning size={22} weight="fill" className="text-arena-primary" />
          <span className="font-heading font-bold tracking-tight text-lg">VIRAL<span className="text-arena-primary">.</span>ARENA</span>
        </div>

        {status.state === "polling" && (
          <div className="text-center py-8">
            <CircleNotch size={40} weight="bold" className="animate-spin text-arena-primary mx-auto mb-4" />
            <div className="font-heading text-2xl font-bold">CONFIRMING PAYMENT</div>
            <div className="text-arena-muted text-sm mt-2">Hang tight — verifying with Stripe. This can take a few seconds.</div>
          </div>
        )}

        {status.state === "paid" && (
          <div className="text-center py-4" data-testid="payment-paid">
            <CheckCircle size={48} weight="fill" className="text-arena-success mx-auto mb-4" />
            <div className="font-heading text-3xl font-bold">PAYMENT COMPLETE</div>

            <div className="mt-6 border border-arena-border p-4 text-left text-xs space-y-2">
              <div className="flex justify-between">
                <span className="text-arena-muted">Order #</span>
                <span className="text-arena-primary font-bold">{status.data?.order_number || "creating..."}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-arena-muted">Amount</span>
                <span>${((status.data?.amount_total || 0) / 100).toFixed(2)} {status.data?.currency?.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-arena-muted">Status</span>
                <span className="text-arena-success">Delivered</span>
              </div>
            </div>

            {status.data?.grant_id ? (
              <Link
                to={`/download/${status.data.grant_id}`}
                data-testid="go-to-download-btn"
                className="mt-6 inline-block w-full bg-arena-primary text-black px-8 py-5 text-sm tracking-widest uppercase font-bold hover:bg-arena-primaryHover"
              >
                → Go to your download
              </Link>
            ) : (
              <div className="mt-6 text-xs text-arena-muted flex items-start gap-2 border border-arena-border p-3 text-left">
                <Package size={18} weight="bold" className="text-arena-primary mt-0.5 flex-shrink-0" />
                <div>
                  Your order is being processed. You&apos;ll receive tracking info by email within 1-2 business days.
                </div>
              </div>
            )}
          </div>
        )}

        {status.state === "expired" && (
          <div className="text-center py-4" data-testid="payment-expired">
            <XCircle size={48} weight="fill" className="text-arena-danger mx-auto mb-4" />
            <div className="font-heading text-2xl font-bold">SESSION EXPIRED</div>
            <div className="text-arena-muted text-sm mt-2">This checkout link has expired. Please try again.</div>
          </div>
        )}

        {status.state === "timeout" && (
          <div className="text-center py-4" data-testid="payment-timeout">
            <CircleNotch size={48} weight="bold" className="text-arena-warning mx-auto mb-4" />
            <div className="font-heading text-2xl font-bold">STILL CONFIRMING</div>
            <div className="text-arena-muted text-sm mt-2">
              Payment is taking longer than expected. You&apos;ll receive an email once confirmed. If you were charged and don&apos;t hear back within 10 min, contact <span className="text-arena-primary">support@pepstyles.store</span>.
            </div>
          </div>
        )}

        {status.state === "error" && (
          <div className="text-center py-4" data-testid="payment-error">
            <XCircle size={48} weight="fill" className="text-arena-danger mx-auto mb-4" />
            <div className="font-heading text-2xl font-bold">SOMETHING WENT WRONG</div>
            <div className="text-arena-muted text-sm mt-2">{status.error}</div>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link to="/" className="text-xs tracking-widest uppercase text-arena-primary hover:underline" data-testid="link-home">
            ← Back home
          </Link>
        </div>
      </div>
    </div>
  );
}
